import { Injectable } from '@angular/core';
import { defer, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { TaskListCharacteristic, TaskListOperation } from 'src/app/models/domain';
import { CharacteristicDataService } from 'src/app/services';
import { CollectionState } from 'src/app/state/collection-state';

@Injectable({ providedIn: 'root' })
export class CharacteristicService {
  private readonly state = new CollectionState<TaskListCharacteristic>();

  public readonly characteristics$ = this.state.items$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;

  public constructor(private readonly characteristicDataService: CharacteristicDataService) {}

  public selectByOperation(operation: TaskListOperation) {
    return this.characteristics$.pipe(
      map((characteristics) =>
        characteristics.filter(
          (characteristic) =>
            characteristic.id === `${operation.id}-${characteristic.characteristic}`
        )
      )
    );
  }

  public populateCharacteristicsByOperation(operation: TaskListOperation) {
    // setLoading inside the fetch branch, not before it: on the cached path no
    // request runs and nothing would clear the flag (`add` is what ends the load).
    const request$ = defer(() => {
      this.state.setLoading(true);

      return this.characteristicDataService.loadByOperation(operation);
    }).pipe(
      tap((characteristics) => {
        this.state.add(...characteristics);
      })
    );

    // Only the current value decides whether to fetch; without take(1) the outer
    // observable re-emits when add() lands and re-enters this branch.
    return this.selectByOperation(operation).pipe(
      take(1),
      switchMap((existing) => (existing.length === 0 ? request$ : of([])))
    );
  }
}
